import { classes } from "portal/theme/theme";
import { COMPARISON_LINE_STROKE_WIDTH } from "./ShootTimeComparisonLine";
import {
  DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX,
  ShootTimeBand,
  ShootTimeFormulaVersion,
} from "portal/utils/shootTime";
import {
  estimateThresholdLabelWidth,
  getChartFooterLabelY,
  getLegendOriginX,
} from "./layout";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import React, {
  FunctionComponent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type LegendEntry = {
  dashed: boolean;
  label: string;
  stroke?: string;
  strokeWidth?: number;
};

const SWATCH_WIDTH = 18;
const SWATCH_GAP = 6;
const ITEM_GAP = 20;
/** Lift the swatch onto the vertical center of 12px alphabetic text. */
const SWATCH_BASELINE_OFFSET = 4;

/** Solid Quick Tune curve, the dashed range on 2.5 charts, then the Carbon Default comparison when present. */
export interface ShootTimeChartLegendProps {
  bands: readonly ShootTimeBand[];
  chartHeight: number;
  className?: string;
  comparisonLabel?: string;
  formulaVersion: ShootTimeFormulaVersion;
}

export const ShootTimeChartLegend: FunctionComponent<
  ShootTimeChartLegendProps
> = ({ bands, chartHeight, className, comparisonLabel, formulaVersion }) => {
  const { t } = useTranslation();
  const {
    palette: {
      chart: { comparisonColor },
      text: { primary },
    },
  } = useTheme();
  const y = getChartFooterLabelY(chartHeight);
  const [labelWidths, setLabelWidths] = useState<
    Readonly<Record<string, number>>
  >({});
  const onTextWidth = useCallback((label: string, width: number) => {
    setLabelWidths((current) =>
      current[label] === width ? current : { ...current, [label]: width }
    );
  }, []);

  const entries = useMemo(() => {
    const quickTuneValues =
      formulaVersion === "2.5"
        ? [DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX]
        : uniqueFineTuneValues(bands);
    const items: LegendEntry[] = quickTuneValues.map((value) => ({
      dashed: false,
      label: t("views.categoryTemplates.shootTime.fineTuneCurveLabel", {
        value,
      }),
    }));
    if (formulaVersion === "2.5") {
      items.push({
        dashed: true,
        label: t("views.categoryTemplates.shootTime.rangeLabel"),
      });
    }
    if (comparisonLabel) {
      items.push({
        dashed: false,
        label: comparisonLabel,
        stroke: comparisonColor,
        strokeWidth: COMPARISON_LINE_STROKE_WIDTH,
      });
    }
    return items;
  }, [bands, comparisonColor, comparisonLabel, formulaVersion, t]);

  let cursor = getLegendOriginX();
  const items = entries.map((entry) => {
    const x = cursor;
    const textWidth =
      labelWidths[entry.label] ?? estimateThresholdLabelWidth(entry.label);
    cursor += SWATCH_WIDTH + SWATCH_GAP + textWidth + ITEM_GAP;
    return (
      <LegendItem
        key={entry.label}
        dashed={entry.dashed}
        label={entry.label}
        stroke={entry.stroke}
        strokeWidth={entry.strokeWidth}
        x={x}
        y={y}
        onTextWidth={onTextWidth}
      />
    );
  });

  return (
    <g className={classes("text-xs", className)} style={{ color: primary }}>
      {items}
    </g>
  );
};

/** First-seen Quick Tune index per band. Legacy bands can disagree. */
const uniqueFineTuneValues = (bands: readonly ShootTimeBand[]): number[] => {
  const seen = new Set<number>();
  const values: number[] = [];
  for (const band of bands) {
    const value = band.formula.fineTuneMultiplier;
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    values.push(value);
  }
  return values;
};

const LegendItem: FunctionComponent<{
  dashed: boolean;
  label: string;
  onTextWidth: (label: string, width: number) => void;
  stroke?: string;
  strokeWidth?: number;
  x: number;
  y: number;
}> = ({ dashed, label, onTextWidth, stroke, strokeWidth, x, y }) => {
  const textRef = useRef<SVGTextElement>(null);

  useLayoutEffect(() => {
    const measuredWidth = textRef.current?.getComputedTextLength() ?? 0;
    if (measuredWidth > 0) {
      onTextWidth(label, measuredWidth);
    }
  }, [label, onTextWidth]);

  const swatchY = y - SWATCH_BASELINE_OFFSET;

  return (
    <>
      <line
        x1={x}
        x2={x + SWATCH_WIDTH}
        y1={swatchY}
        y2={swatchY}
        stroke={stroke}
        strokeWidth={strokeWidth ?? (dashed ? 1.5 : 2)}
        strokeDasharray={dashed ? "4 4" : undefined}
        className={stroke ? undefined : "stroke-current"}
      />
      <text
        ref={textRef}
        x={x + SWATCH_WIDTH + SWATCH_GAP}
        y={y}
        textAnchor="start"
        dominantBaseline="auto"
        className="fill-current"
      >
        {label}
      </text>
    </>
  );
};
